import { Controller } from '@hotwired/stimulus';

const BUTTON = [
  'flex h-8 w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-edge',
  'bg-canvas pr-2.5 pl-3 text-left text-sm text-fg transition-colors duration-100',
  'focus:border-accent focus:outline-2 focus:-outline-offset-1 focus:outline-accent',
].join(' ');

const PANEL = [
  'absolute z-20 mt-1 flex w-full flex-col gap-1 rounded-md border border-edge bg-canvas p-1',
  'shadow-lg',
].join(' ');

const QUERY = [
  'h-7 w-full rounded border border-edge bg-canvas px-2 text-sm text-fg',
  'focus:border-accent focus:outline-none',
].join(' ');

const OPTION = 'cursor-pointer rounded px-2 py-1 text-left text-sm text-fg hover:bg-subtle';
const OPTION_ACTIVE = 'bg-subtle';
const EMPTY = 'px-2 py-1 text-sm text-muted';

export default class extends Controller {
  static values = { placeholder: String, empty: String, buttonClass: String, panelClass: String };

  connect() {
    this.select = this.element.querySelector('select');

    if (!this.select) return;

    this.#build();
    this.#renderLabel();

    this.outsideHandler = (event) => {
      if (!this.element.contains(event.target)) this.#close();
    };

    document.addEventListener('click', this.outsideHandler);
  }

  disconnect() {
    if (this.outsideHandler) document.removeEventListener('click', this.outsideHandler);
  }

  toggle(event) {
    event.preventDefault();

    if (this.panel.hidden) this.#open();
    else this.#close();
  }

  filter() {
    this.#renderOptions(this.query.value.trim().toLowerCase());
  }

  navigate(event) {
    const keys = { ArrowDown: 1, ArrowUp: -1 };

    if (event.key === 'Escape') return this.#close();

    if (event.key === 'Enter') {
      event.preventDefault();

      return this.#active()?.click();
    }

    if (!(event.key in keys)) return;

    event.preventDefault();
    this.#move(keys[event.key]);
  }

  #build() {
    this.element.classList.add('relative');
    this.select.parentElement.hidden = true;
    this.select.tabIndex = -1;

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = this.hasButtonClassValue ? this.buttonClassValue : BUTTON;
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.dataset.action = 'click->search-select#toggle';

    const label = document.createElement('span');
    label.className = 'truncate';
    label.dataset.role = 'label';

    const caret = document.createElement('span');
    caret.className = 'shrink-0';
    caret.setAttribute('aria-hidden', 'true');
    caret.textContent = '▾';

    this.button.append(label, caret);

    this.panel = document.createElement('div');
    this.panel.className = this.hasPanelClassValue ? this.panelClassValue : PANEL;
    this.panel.hidden = true;

    this.query = document.createElement('input');
    this.query.type = 'search';
    this.query.className = QUERY;
    this.query.placeholder = this.placeholderValue;
    this.query.dataset.action = 'input->search-select#filter keydown->search-select#navigate';

    this.list = document.createElement('div');
    this.list.className = 'flex max-h-56 flex-col overflow-y-auto';
    this.list.setAttribute('role', 'listbox');

    this.panel.append(this.query, this.list);
    this.element.append(this.button, this.panel);
  }

  #open() {
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.query.value = '';
    this.#renderOptions('');
    this.query.focus();
  }

  #close() {
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
  }

  #renderOptions(needle) {
    const matches = [...this.select.options].filter((option) => option.text.toLowerCase().includes(needle));

    this.list.replaceChildren(...matches.map((option) => this.#option(option)));

    if (matches.length > 0) return this.#paint(this.list.firstElementChild);

    const empty = document.createElement('p');

    empty.className = EMPTY;
    empty.textContent = this.emptyValue;
    this.list.replaceChildren(empty);
  }

  #option(option) {
    const node = document.createElement('button');

    node.type = 'button';
    node.className = OPTION;
    node.textContent = option.text;
    node.dataset.value = option.value;
    node.setAttribute('role', 'option');
    node.addEventListener('click', () => this.#pick(option.value));

    return node;
  }

  #pick(value) {
    this.select.value = value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));

    this.#renderLabel();
    this.#close();
    this.button.focus();
  }

  #renderLabel() {
    const selected = this.select.selectedOptions[0];

    this.button.querySelector('[data-role="label"]').textContent = selected ? selected.text : '';
  }

  #options() {
    return [...this.list.querySelectorAll('[role="option"]')];
  }

  #active() {
    return this.#options().find((node) => node.classList.contains(OPTION_ACTIVE));
  }

  #move(step) {
    const nodes = this.#options();

    if (nodes.length === 0) return;

    const index = nodes.indexOf(this.#active());
    const next = nodes[(index + step + nodes.length) % nodes.length];

    this.#paint(next);
    next.scrollIntoView({ block: 'nearest' });
  }

  #paint(node) {
    for (const option of this.#options()) option.classList.remove(OPTION_ACTIVE);

    node?.classList.add(OPTION_ACTIVE);
  }
}
